import { useCallback, useEffect, useRef, useState } from 'react';
import type { Worker } from 'tesseract.js';

import { preprocessForOcr } from './preprocess.js';

/**
 * OCR da etiqueta, no proprio dispositivo.
 *
 * O codigo de barras identifica o objeto; o destino esta impresso como texto.
 * Sem esta leitura, o porteiro continuaria escolhendo a unidade na mao — que e
 * a digitacao que mais custa tempo no balcao.
 *
 * Roda no cliente, e nao no servidor, por tres motivos: a foto da etiqueta
 * (com nome e endereco de morador) nunca sai do dispositivo, o resultado
 * aparece antes de qualquer ida a rede, e a portaria continua registrando
 * encomenda com a internet oscilando.
 *
 * O worker so e criado no primeiro uso. Sao alguns MB de WebAssembly e do
 * idioma portugues — cobrar isso de quem so quer abrir o painel seria pessimo.
 * O navegador guarda em cache, entao o custo e uma vez por dispositivo.
 */

export type OcrStatus = 'idle' | 'loading' | 'ready' | 'reading' | 'error';

export interface LabelOcr {
  status: OcrStatus;
  /** 0..1 — usado na barra durante o download do idioma. */
  progress: number;
  message: string | null;
  read: (canvas: HTMLCanvasElement) => Promise<string | null>;
}

export function useLabelOcr(): LabelOcr {
  const workerRef = useRef<Worker | null>(null);
  // Guardar a PROMESSA, e nao so o worker, evita criar dois workers quando o
  // porteiro toca "ler etiqueta" duas vezes antes do primeiro terminar.
  const bootRef = useRef<Promise<Worker> | null>(null);
  const [status, setStatus] = useState<OcrStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      void workerRef.current?.terminate();
      workerRef.current = null;
      bootRef.current = null;
    };
  }, []);

  const boot = useCallback(async (): Promise<Worker> => {
    if (workerRef.current) return workerRef.current;
    if (bootRef.current) return bootRef.current;

    setStatus('loading');
    setProgress(0);

    const promise = (async () => {
      const { createWorker, PSM } = await import('tesseract.js');
      // Os tres caminhos apontam para o NOSSO servidor. Sem eles, o
      // tesseract.js busca worker, core WASM e modelo no cdn.jsdelivr.net em
      // runtime — codigo de terceiro rodando na aba do porteiro, com sessao
      // aberta, sem aparecer no package.json nem no npm audit. Os arquivos sao
      // copiados do node_modules no build (frontend/scripts/copy-tesseract.mjs).
      //
      // `corePath` e `langPath` sao PASTAS de proposito: o tesseract monta o
      // nome do arquivo do core conforme o suporte a SIMD do aparelho, e o do
      // modelo conforme o idioma. Apontar para um arquivo fixo tiraria essa
      // escolha dele.
      const worker = await createWorker('por', 1, {
        workerPath: '/tesseract/worker.min.js',
        corePath: '/tesseract',
        langPath: '/tesseract',
        logger: (info: { status: string; progress: number }) => {
          if (info.status === 'recognizing text' || info.status.startsWith('loading')) {
            setProgress(info.progress);
          }
        },
      });

      await worker.setParameters({
        // O padrao e AUTO, que primeiro tenta descobrir se a imagem e uma
        // pagina, uma tabela ou texto solto. Depois do recorte pela moldura nos
        // JA SABEMOS a resposta: e um bloco unico de endereco. Dizer isso evita
        // que ele decida sozinho que as linhas curtas do endereco sao colunas
        // de uma tabela e as leia na ordem errada.
        tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
        // "APTO 302" precisa continuar com o espaco: sem isto o Tesseract cola
        // rotulo e numero em "APTO302" e o padrao do parser deixa de casar.
        preserve_interword_spaces: '1',
      });

      workerRef.current = worker;
      setStatus('ready');
      return worker;
    })();

    bootRef.current = promise;

    try {
      return await promise;
    } catch (error) {
      bootRef.current = null;
      setStatus('error');
      setMessage('Nao foi possivel carregar o leitor de texto.');
      throw error;
    }
  }, []);

  const read = useCallback(
    async (canvas: HTMLCanvasElement): Promise<string | null> => {
      try {
        const worker = await boot();
        setStatus('reading');
        setProgress(0);

        // Cinza, contraste esticado e ampliado — ver `preprocess.ts`. A imagem
        // crua era a causa de so o codigo de rastreio ser lido.
        const result = await worker.recognize(preprocessForOcr(canvas));
        setStatus('ready');
        setMessage(null);

        const text = result.data.text.trim();
        return text.length > 0 ? text : null;
      } catch {
        setStatus('error');
        setMessage('Falha ao ler o texto da etiqueta.');
        return null;
      }
    },
    [boot],
  );

  return { status, progress, message, read };
}
